import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArtSetCard } from "@/components/studio/ArtSetCard";
import { ConvertStatus } from "@/components/studio/ConvertStatus";
import { AIPanel } from "@/components/studio/AIPanel";
import { ChaptersView } from "@/components/studio/ChaptersView";
import { DetailsForm } from "@/components/studio/DetailsForm";
import { DownloadTiles } from "@/components/studio/DownloadTiles";
import { EditionCard } from "@/components/studio/EditionCard";
import { InstagramCard } from "@/components/studio/InstagramCard";
import { PreviewPane } from "@/components/studio/PreviewPane";
import { ProductHeader } from "@/components/studio/ProductHeader";
import { StoreListingTab } from "@/components/studio/StoreListingTab";
import { FactCheckTab } from "@/components/studio/FactCheckTab";
import { api, errMsg } from "@/lib/api";

const JOBS = ["update_job", "illustration_job", "ai_job", "chapter_art_job", "art_set_job", "fit_job", "fact_job", "store_sync"];
const FIELDS = ["title", "subtitle", "series", "category_id", "product_type", "back_headline", "back_blurb", "issue_no", "target_pages", "price", "listing", "sections"];
const FALLBACK = { name: "Plum & Gold", dark: "#38171E", accent: "#D4AF37", tint: "#F3E6E8" };
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random()));

export default function Studio() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [cats, setCats] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [version, setVersion] = useState(0);
  const [tab, setTab] = useState("manuscript");
  const pRef = useRef(null);
  pRef.current = p;

  const reload = useCallback(async () => {
    const r = await api.get(`/products/${id}`);
    setP(r.data);
    setDirty(false);
    setVersion((v) => v + 1);
  }, [id]);

  useEffect(() => {
    reload().catch(() => nav("/library"));
    api.get("/categories").then((r) => setCats(r.data));
  }, [reload, nav]);

  const set = (patch) => {
    setP((cur) => ({ ...cur, ...patch }));
    setDirty(true);
  };

  const save = useCallback(async (quiet) => {
    const cur = pRef.current;
    const EMPTY = { sections: [], store_ids: [], listing: null, target_pages: null };
    const body = Object.fromEntries(FIELDS.map((k) => [k, cur[k] ?? (k in EMPTY ? EMPTY[k] : "")]));
    try {
      const r = await api.put(`/products/${id}`, body);
      setP((c) => ({ ...c, ...r.data }));
      setDirty(false);
      setVersion((v) => v + 1);
      if (!quiet) toast.success("Saved");
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    }
  }, [id]);

  const ensureSaved = useCallback(() => save(true), [save]);

  const busyJob = JOBS.some((k) => p?.[k]?.status === "running");
  const [poll, setPoll] = useState(0);
  useEffect(() => {
    if (!busyJob && !poll) return;
    const t = setInterval(async () => {
      const r = await api.get(`/products/${id}`);
      const keep = pRef.current;
      setP((c) => ({ ...c, ...r.data, ...(dirtyRef.current ? Object.fromEntries(FIELDS.map((k) => [k, keep[k]])) : {}) }));
      const running = JOBS.some((k) => r.data[k]?.status === "running");
      if (keep?.art_set_job?.status === "running" && r.data.art_set_job?.status === "done") {
        const errs = r.data.art_set_job.errors?.length || 0;
        toast[errs ? "error" : "success"](errs ? `Set finished — ${errs} image(s) failed` : "Illustrated set ready — click Rebuild files to use it");
      } else if (keep?.chapter_art_job?.status === "running" && r.data.chapter_art_job?.status === "done") {
        const errs = r.data.chapter_art_job.errors?.length || 0;
        toast[errs ? "error" : "success"](errs ? `${errs} illustration(s) failed` : "Chapter illustrations ready — rebuild files to include them");
      }
      if (keep?.store_sync?.status === "running" && r.data.store_sync?.status !== "running") {
        if (r.data.store_sync?.status === "done") toast.success(`On softspinepress.com as ${r.data.store_sync.store_status === "live" ? "Live" : "Draft"}`);
        else toast.error(`softspinepress.com: ${r.data.store_sync?.error || "sync failed"}`);
      }
      if (keep?.fact_job?.status === "running" && r.data.fact_job?.status === "done") {
        const n = Object.values(r.data.fact_check?.sections || {}).flatMap((x) => x.claims).filter((c) => c.status !== "verified" && !c.resolution).length;
        toast[n ? "message" : "success"](n ? `Fact check done — ${n} claim${n > 1 ? "s" : ""} need your review` : "Fact check done — everything verified");
      }
      if (keep?.fit_job?.status === "running" && r.data.fit_job?.status === "done") {
        toast[r.data.fit_job.on_target ? "success" : "message"](`Fitted: ${r.data.fit_job.before} → ${r.data.fit_job.after} pages · files rebuilt`);
      }
      if (keep?.fit_job?.status === "running" && r.data.fit_job?.status === "failed") toast.error(r.data.fit_job.error);
      if (keep?.ai_job?.status === "running" && r.data.ai_job?.status === "done") {
        toast.success(`Converted — ${r.data.ai_job.added} sections added`);
        setTab("manuscript");
      }
      if (keep?.ai_job?.status === "running" && r.data.ai_job?.status === "failed") toast.error(r.data.ai_job.error);
      if (!running) {
        setPoll(0);
        setVersion((v) => v + 1);
      }
    }, 4000);
    return () => clearInterval(t);
  }, [busyJob, poll, id]);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  const updateSection = (i, s) => set({ sections: p.sections.map((x, j) => (j === i ? s : x)) });
  const moveSection = (i, d) => {
    const arr = [...p.sections];
    [arr[i], arr[i + d]] = [arr[i + d], arr[i]];
    set({ sections: arr });
  };
  const deleteSection = (i) => set({ sections: p.sections.filter((_, j) => j !== i) });
  const addSection = () => set({ sections: [...(p.sections || []), { id: newId(), heading: "New section", body: "<p></p>" }] });

  const remove = async () => {
    if (!window.confirm(`Delete “${p.title}”? This cannot be undone.`)) return;
    await api.delete(`/products/${id}`);
    nav("/library");
  };

  if (!p) return <div className="p-12 font-display italic text-2xl text-[#5C5255]">Fetching the manuscript…</div>;
  const cat = cats.find((c) => c.id === p.category_id);
  const theme = cat?.theme || FALLBACK;
  const patch = (x) => setP((c) => ({ ...c, ...x }));

  return (
    <div className="pb-16">
      <div className="px-6 md:px-10 pt-6">
        <Link to="/library" className="text-xs text-[#5C5255] flex items-center gap-1 hover:text-[#5E2B38]" data-testid="studio-back-link"><ArrowLeft size={12} /> Library</Link>
      </div>
      <ProductHeader p={p} cat={cat} theme={theme} dirty={dirty} onSave={() => save(false).catch(() => {})} onDelete={remove} onPatch={patch} setIssue={(v) => set({ issue_no: v })} onCatSaved={() => api.get("/categories").then((r) => setCats(r.data))} />
      <div className="px-6 md:px-10 mt-8 space-y-8">
        <EditionCard p={p} theme={theme} ensureSaved={ensureSaved} onStarted={() => setPoll(1)} />
        <ArtSetCard p={p} cat={cat} theme={theme} ensureSaved={ensureSaved}
          onStarted={(j) => { setP((c) => ({ ...c, art_set_job: j })); setPoll(1); }}
          onRefresh={async () => { const r = await api.get(`/products/${id}`); setP((c) => ({ ...c, art_style: r.data.art_style })); }} />
        <InstagramCard p={p} theme={theme} version={version} onPatch={patch} onStarted={() => setPoll(1)} />
        <DownloadTiles p={p} theme={theme} ensureSaved={ensureSaved} onPatch={patch} onStarted={() => setPoll(1)} />
        <ConvertStatus p={p} ensureSaved={ensureSaved} onChanged={reload} />
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none bg-transparent border-b border-[#2A2325]/10 w-full justify-start h-auto p-0 mb-8">
            {[["manuscript", `Chapters (${p.sections?.length || 0})`], ["ai", "AI writer"], ["facts", "Fact check"], ["details", "Covers & details"], ["listing", "Store listing"], ["preview", "Preview"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} className="rounded-none px-4 py-3 data-[state=active]:shadow-none data-[state=active]:bg-transparent border-b-2 border-transparent data-[state=active]:border-current" style={tab === v ? { color: theme.dark } : {}} data-testid={`studio-tab-${v}`}>{l}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="manuscript">
            <ChaptersView p={p} theme={theme} onChange={updateSection} onMove={moveSection} onDelete={deleteSection} onAdd={addSection} ensureSaved={ensureSaved} onOpenAI={() => setTab("ai")}
              onArtStarted={(j) => { setP((c) => ({ ...c, chapter_art_job: j })); setPoll(1); }}
              onArtChanged={async () => { const r = await api.get(`/products/${id}`); setP((c) => ({ ...c, chapter_art: r.data.chapter_art || {} })); }} />
          </TabsContent>
          <TabsContent value="ai" className="max-w-4xl">
            <AIPanel product={p} ensureSaved={ensureSaved} onProgress={reload} onDone={() => setTab("manuscript")} onStarted={(j) => { setP((c) => ({ ...c, ai_job: j })); setPoll(1); }} />
          </TabsContent>
          <TabsContent value="facts" className="max-w-4xl">
            <FactCheckTab key={p.fact_check?.checked_at || "none"} p={p} theme={theme} ensureSaved={ensureSaved} onStarted={() => setPoll(1)} onPatch={patch} dirty={dirty} />
          </TabsContent>
          <TabsContent value="details" className="max-w-4xl">
            <DetailsForm p={p} set={set} cats={cats} />
          </TabsContent>
          <TabsContent value="listing" className="max-w-4xl">
            <StoreListingTab p={p} set={set} />
          </TabsContent>
          <TabsContent value="preview" className="max-w-[1500px]">
            <PreviewPane productId={p.id} version={version} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
